import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown, FiCheck, FiEye } from 'react-icons/fi';
import Modal from '../Modal';
import './style.css';

const DROPDOWN_MAX_HEIGHT = 260; // altura estimada do dropdown em px

export default function MultiSelect({
  id,
  label,
  options = [],
  value = [],
  onChange,
  placeholder = 'SELECIONE...',
  className = '',
  icon: Icon,
  disabled = false,
  forceHeight = '46px',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [coords, setCoords] = useState({ top: null, bottom: null, left: 0, width: 0, openUpward: false });
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const isClosingByBack = useRef(false);

  const normalizedOptions = useMemo(() => (Array.isArray(options) ? options : []), [options]);
  const selectedValues = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const selectedOptions = useMemo(() => {
    return selectedValues
      .map(val => normalizedOptions.find(opt => String(opt.value) === String(val)))
      .filter(Boolean);
  }, [selectedValues, normalizedOptions]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return normalizedOptions;
    const term = searchTerm.toLowerCase();
    return normalizedOptions.filter(option =>
      String(option.label).toLowerCase().includes(term)
    );
  }, [normalizedOptions, searchTerm]);

  const isSearchEnabled = normalizedOptions.length >= 10;

  useEffect(() => {
    if (isOpen) {
      window.history.pushState({ multiSelectOpen: id || 'multiselect' }, '');
      
      const handlePopState = () => {
        isClosingByBack.current = true;
        setIsOpen(false);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
        const currentState = window.history.state;
        if (!isClosingByBack.current && currentState && currentState.multiSelectOpen === (id || 'multiselect')) {
          window.history.back();
        }
        isClosingByBack.current = false;
      };
    }
  }, [isOpen, id]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        wrapperRef.current && 
        !wrapperRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        document.body.setAttribute('data-dropdown-closed', 'true');
        setTimeout(() => {
          document.body.removeAttribute('data-dropdown-closed');
        }, 200);
        setIsOpen(false);
        setSearchTerm('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const updatePosition = () => {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const openUpward = spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;

        if (openUpward) {
          setCoords({
            // fixed: bottom da viewport menos o topo do componente
            bottom: window.innerHeight - rect.top + 4,
            top: null,
            left: rect.left,
            width: rect.width,
            openUpward: true,
          });
        } else {
          setCoords({
            top: rect.bottom + 4,
            bottom: null,
            left: rect.left,
            width: rect.width,
            openUpward: false,
          });
        }
      };

      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);

      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [isOpen]);

  function handleContainerClick() {
    if (disabled) return;
    if (!isOpen) {
      setIsOpen(true);
    }
  }

  function handleInputChange(e) {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  }

  function handleOptionClick(optionValue) {
    if (disabled) return;

    // Mantém o dropdown sempre aberto ao selecionar/deselecionar opções
    if (!isOpen) {
      setIsOpen(true);
    }

    const strValue = String(optionValue);
    const isSelected = selectedValues.some(val => String(val) === strValue);
    
    let newValue;
    if (isSelected) {
      newValue = selectedValues.filter(val => String(val) !== strValue);
    } else {
      newValue = [...selectedValues, strValue];
    }

    if (onChange) {
      onChange(newValue);
    }
  }

  function handleKeyDown(e) {
    // Backspace logic removed as chips are hidden
  }

  const dropdownStyle = {
    position: 'fixed',
    left: coords.left,
    width: coords.width,
    zIndex: 99999,
    ...(coords.openUpward
      ? { bottom: coords.bottom }
      : { top: coords.top }),
  };

  return (
    <div className={`field ${className}`} ref={wrapperRef}>
      {label && (
        <label className="field-label" htmlFor={id}>
          {label}
        </label>
      )}

      <div className="multiselect-wrapper">
        <div 
          ref={containerRef}
          style={{height: forceHeight}}
          className={`multiselect-container ${isOpen ? 'focused' : ''} ${Icon ? 'has-icon' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={handleContainerClick}
        >
          {Icon && (
            <div className="input-icon">
              <Icon size={18} />
            </div>
          )}

          {selectedValues.length > 0 && !searchTerm && (
            <div className="multiselect-summary">
              ({selectedValues.length}) selecionados
            </div>
          )}

          <input
            ref={inputRef}
            id={id}
            type="text"
            className="multiselect-input"
            value={searchTerm}
            onChange={isSearchEnabled ? handleInputChange : undefined}
            onKeyDown={handleKeyDown}
            onFocus={isSearchEnabled ? () => setIsOpen(true) : undefined}
            placeholder={selectedValues.length === 0 ? placeholder : ''}
            disabled={disabled}
            readOnly={!isSearchEnabled}
            tabIndex={isSearchEnabled ? 0 : -1}
            style={!isSearchEnabled ? { pointerEvents: 'none' } : undefined}
          />

          {selectedOptions.length > 0 && (
            <button
              type="button"
              className="multiselect-eye-button"
              onClick={(e) => {
                e.stopPropagation();
                if (!disabled) {
                  setIsOpen(false);
                  setSearchTerm('');
                  setIsViewModalOpen(true);
                }
              }}
            >
              <FiEye size={18} />
            </button>
          )}

          <div className="multiselect-chevron">
            <FiChevronDown size={20} />
          </div>
        </div>

        {isOpen && !disabled && createPortal(
          <div 
            ref={dropdownRef}
            className={`multiselect-dropdown ${coords.openUpward ? 'open-upward' : ''}`}
            style={dropdownStyle}
          >
            <div className="multiselect-options">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => {
                  const isSelected = selectedValues.some(val => String(val) === String(option.value));
                  return (
                    <div
                      key={option.value}
                      className={`multiselect-option ${isSelected ? 'selected' : ''}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={() => handleOptionClick(option.value)}
                    >
                      <span>{option.label}</span>
                      {isSelected && <FiCheck size={16} />}
                    </div>
                  );
                })
              ) : (
                <div className="multiselect-empty">
                  Nenhuma opção encontrada
                </div>
              )}
            </div>
          </div>,
          document.body
        )}

        <Modal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          title="Itens selecionados"
          size="sm"
        >
          <div className="multiselect-view-modal-body">
            {selectedOptions.length === 0 ? (
              <p className="multiselect-view-empty">
                Nenhuma opção selecionada
              </p>
            ) : (
              <div className="multiselect-view-list">
                {selectedOptions.map(option => (
                  <div key={option.value} className="multiselect-view-item">
                    <span className="multiselect-view-label">
                      {option.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
}
